import { useEffect, useState, useMemo } from "react";
import { ReactSortable, type SortableEvent } from "react-sortablejs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ConfTableRow from "@components/feature/roster_configure/ConfTableRow";
import ConfTableAddRow from "@components/feature/roster_configure/ConfTableAddRow";
import LoadingSpiner from "@components/ui/LoadingSpiner";

import type * as type from "@vTypes";
import * as req from "@script/req";
import { toast } from "@/components/toast/CustomToast";
import CustomModal from "@/components/modal/CustomModal";
import {
  useShiftsQuery,
  type ShiftReturnType,
} from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftManageQuery } from "@/hooks/roster-craete/useShiftManageQuery";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";

interface ConfTab0Props {
  isAdmin: boolean;
  selectedGroupId: string | null;
}

export interface LocalShiftType {
  id: number;
  name: string;
  shift_id: string;
  type: string;
  sequence: number;
  color: string;
  allday: number;
  auto_schedule: number;
  shift_gb: string | null;
  start_time: string | null;
  end_time: string | null;
  duration: number | null;
  default_shift: string | null;
  show_in_preference?: boolean;
  off_swap_target?: boolean;
}

export default function ConfTab1({ isAdmin, selectedGroupId }: ConfTab0Props) {
  const queryClient = useQueryClient();
  const [addNewShifts, setAddNewShifts] = useState<boolean>(false);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // 로컬 상태 (서버 데이터 동기화)
  const [localShifts, setLocalShifts] = useState<ShiftReturnType>([]);

  // 파생 상태
  const shiftIdFlatMap = useMemo(
    () => localShifts.map((s) => s.shift_id),
    [localShifts]
  );

  const targetGroupId = isAdmin ? (selectedGroupId ?? "") : "";

  const { data: shiftsData, isLoading: isShiftsLoading } =
    useShiftsQuery(targetGroupId);

  const { data: shiftManageData, isLoading: isManageLoading } =
    useShiftManageQuery(targetGroupId);
  console.log("shiftManageData", shiftManageData);

  // 서버 데이터 -> 로컬 상태 동기화 + 디버깅 로그
  useEffect(() => {
    if (shiftsData) setLocalShifts(shiftsData);
    else setLocalShifts([]);
  }, [shiftsData]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data: type.AddShift) => req.addShift(data, targetGroupId),
    onSuccess: () => {
      toast.success("추가되었습니다");
      setAddNewShifts(false);
      // ["SHIFTS"]로 시작하는 모든 쿼리 무효화 (null, "", groupId 모두 커버)
      queryClient.invalidateQueries({
        queryKey: ["SHIFTS"],
      });
      queryClient.invalidateQueries({
        queryKey: ["shiftManager"],
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: type.UpdateShift) =>
      req.updateShift(data, targetGroupId),
    onSuccess: () => {
      // ["SHIFTS"]로 시작하는 모든 쿼리 무효화 (null, "", groupId 모두 커버)
      queryClient.invalidateQueries({
        queryKey: ["SHIFTS"],
      });
      toast.success("수정되었습니다.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (shiftId: string) => req.deleteShift(shiftId, targetGroupId),
    onSuccess: () => {
      toast.success("삭제되었습니다.");
      // ["SHIFTS"]로 시작하는 모든 쿼리 무효화 (null, "", groupId 모두 커버)
      queryClient.invalidateQueries({
        queryKey: ["SHIFTS"],
      });
      setIsConfirmModalOpen(false);
      setDeleteTargetId(null);
    },
    onError: () => {
      if (shiftsData) setLocalShifts([...shiftsData]);
      setIsConfirmModalOpen(false);
      setDeleteTargetId(null);
    },
  });

  const saveManageMutation = useMutation({
    mutationFn: (payload) => req.saveShiftManage(payload, targetGroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shiftManager", targetGroupId],
      });
    },
  });

  // defaultCode: 메인 시프트 (default_shift 있음, 서브코드 제외) - sequence 순
  const offSwapTargetMutation = useMutation({
    mutationFn: async ({
      target,
      previousTargets,
    }: {
      target: type.UpdateShift;
      previousTargets: type.UpdateShift[];
    }) => {
      if (previousTargets.length > 0) {
        await Promise.all(
          previousTargets.map((previous) =>
            req.updateShift(
              {
                ...previous,
                off_swap_target: false,
              },
              targetGroupId
            )
          )
        );
      }

      return req.updateShift(
        {
          ...target,
          off_swap_target: Boolean(target.off_swap_target),
        },
        targetGroupId
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["SHIFTS"],
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configVersions(targetGroupId ?? ""),
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.rosterConfigVersions(targetGroupId ?? ""),
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configData(targetGroupId ?? ""),
      });
      toast.success("OFF 대체 코드가 변경되었습니다.");
    },
    onError: () => {
      if (shiftsData) setLocalShifts([...shiftsData]);
    },
  });

  const offSwapOffShiftGb = useMemo(
    () => localShifts.find((shift) => shift.default_shift === "O")?.shift_gb,
    [localShifts]
  );

  const isOffSwapCandidate = (shift: {
    type: string;
    default_shift?: string | null;
    shift_gb?: string | null;
  }) =>
    shift.type !== "근무" &&
    shift.default_shift !== "O" &&
    !(offSwapOffShiftGb && shift.shift_gb === offSwapOffShiftGb);

  const handleUpdateShift = (targetShift: type.UpdateShift) => {
    const nextShift = isOffSwapCandidate(targetShift)
      ? targetShift
      : {
          ...targetShift,
          off_swap_target: false,
        };
    const originalShift = localShifts.find((shift) => shift.id === nextShift.id);
    const offSwapTargetChanged =
      Boolean(originalShift?.off_swap_target) !==
      Boolean(nextShift.off_swap_target);
    const previousTargets = nextShift.off_swap_target
      ? localShifts.filter(
          (shift) =>
            shift.off_swap_target &&
            shift.id !== nextShift.id &&
            isOffSwapCandidate(shift)
        )
      : [];

    setLocalShifts((prev) =>
      prev.map((shift) => ({
        ...shift,
        off_swap_target:
          shift.id === nextShift.id
            ? Boolean(nextShift.off_swap_target)
            : nextShift.off_swap_target &&
                isOffSwapCandidate(shift)
              ? false
              : shift.off_swap_target,
      }))
    );

    if (nextShift.off_swap_target || offSwapTargetChanged) {
      offSwapTargetMutation.mutate({
        target: nextShift,
        previousTargets: previousTargets as type.UpdateShift[],
      });
      return;
    }

    updateMutation.mutate(nextShift);
  };

  const defaultCode = useMemo(
    () => getDefaultShiftsSortedBySequence(shiftsData || []),
    [shiftsData]
  );

  // defaultShiftManage: 나머지 시프트 (원본 순서, 드래그 가능)
  const defaultShiftManage = useMemo(() => {
    const defaultIds = new Set(defaultCode.map((s) => s.shift_id));
    return (localShifts || []).filter((s) => !defaultIds.has(s.shift_id));
  }, [localShifts, defaultCode]);

  const sequenceMutation = useMutation({
    mutationFn: ({ id, seq }: { id: string; seq: number }) =>
      req.updateShiftSequence(id, seq, targetGroupId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.shifts.list(targetGroupId),
      }),
  });

  const handleManageDragEnd = (evt: SortableEvent) => {
    if (
      evt.oldIndex === undefined ||
      evt.newIndex === undefined ||
      evt.oldIndex < 0 ||
      evt.oldIndex >= defaultShiftManage.length
    )
      return;

    const shiftId = defaultShiftManage[evt.oldIndex].shift_id;
    const newSequence = defaultCode.length + evt.newIndex + 1;
    sequenceMutation.mutate({ id: shiftId, seq: newSequence });
  };

  const handleManageSetList = (newList: ShiftReturnType) => {
    setLocalShifts([...defaultCode, ...newList]);
  };

  const handleDelete = (shiftId: string) => {
    setDeleteTargetId(shiftId);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteTargetId) {
      deleteMutation.mutate(deleteTargetId);
    }
  };

  if (isShiftsLoading || isManageLoading)
    return (
      <div className="flex min-h-[200px] w-full items-center justify-center py-12">
        <LoadingSpiner msg="근무코드 설정을 불러오는 중..." />
      </div>
    );

  return (
    <div style={{ width: "fit-content", alignSelf: "center" }}>
      <div className="conf_shiftCode_buttonGroup">
        <button
          type="button"
          className={`conf_addRowButton ${addNewShifts && "disabled"}`}
          disabled={addNewShifts}
          onClick={() => setAddNewShifts(true)}
        >
          <span className="conf_addRowButton_plusIcon"></span>
          근무코드 추가하기
        </button>
      </div>
      <div className="conf_shiftCode__table">
        <div className="conf_shiftCode__gridTemplate conf_shiftCode__thead">
          <div></div>
          <div className="font-pretendard">근무코드</div>
          <div className="font-pretendard">이름</div>
          <div className="font-pretendard th_type">구분</div>
          <div className="font-pretendard">근무시간</div>
          <div className="font-pretendard th_type">타입</div>
          <div className="font-pretendard">OFF 대체</div>
          <div className="font-pretendard">원티드 요청시 반영</div>
          <div className="font-pretendard">색상</div>
          <div className="font-pretendard">수정</div>
          <div></div>
        </div>
        <div className="conf_shiftCode__tbody">
          {addNewShifts && (
            <ConfTableAddRow
              setAddNewShifts={setAddNewShifts}
              addShift={(data) => addMutation.mutate(data)}
              currentShiftManage={shiftManageData || []}
              saveShiftManage={(payload) => saveManageMutation.mutate(payload)}
              shiftIdFlatMap={shiftIdFlatMap}
            />
          )}
          {/* defaultCode: 메인 시프트 (sequence 순, 정렬 불가) — 근무코드·이름 등 수정, 구분/타입은 고정 */}
          {defaultCode.map((arr) => (
            <ConfTableRow
              key={arr.shift_id}
              data={arr}
              updateShift={handleUpdateShift}
              deleteShift={(shift_id) => handleDelete(shift_id)}
              currentShiftManage={shiftManageData || []}
              saveShiftManage={(payload) => saveManageMutation.mutate(payload)}
              shiftIdFlatMap={shiftIdFlatMap}
              offSwapOffShiftGb={offSwapOffShiftGb}
              disableDrag
            />
          ))}
          {/* defaultShiftManage: 나머지 시프트 (드래그 가능) */}
          <ReactSortable
            tag="div"
            handle=".conf_button__handler"
            animation={100}
            list={defaultShiftManage as unknown as { id: string }[]}
            setList={(newList) =>
              handleManageSetList(newList as unknown as ShiftReturnType)
            }
            onEnd={handleManageDragEnd}
          >
            {defaultShiftManage.map((arr) => (
              <ConfTableRow
                key={arr.shift_id}
                data={arr}
                updateShift={handleUpdateShift}
                deleteShift={(shift_id) => handleDelete(shift_id)}
                currentShiftManage={shiftManageData || []}
                saveShiftManage={(payload) =>
                  saveManageMutation.mutate(payload)
                }
                shiftIdFlatMap={shiftIdFlatMap}
                offSwapOffShiftGb={offSwapOffShiftGb}
              />
            ))}
          </ReactSortable>
        </div>
      </div>

      {/* 삭제 컨펌 팝업 */}
      <CustomModal.Confirm
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={handleConfirmDelete}
        isPending={deleteMutation.isPending}
        title="정말로 이 근무코드를 삭제하시겠습니까?"
        pendingMsg="삭제 중입니다..."
      />
    </div>
  );
}
